import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { ApiRequestError } from '../../utils/apiRequestError';
import {
  productGroupService,
  ProductGroup,
  ProductGroupImportPreview,
  ProductGroupTableRow,
} from '../../services/productGroupService';
import { fuzzy_tokens, product_label_matches_search } from '../../utils/productSearchMatch';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const ProductGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: auth_loading } = useAuth();
  const { hasPermission, loading: permissions_loading } = usePermissions();
  const has_access =
    hasPermission('products.manage_groups') ||
    (user && (user.role_id === 1 || user.role_name === 'Super Admin'));

  const [groups, set_groups] = useState<ProductGroup[]>([]);
  const [rows, set_rows] = useState<ProductGroupTableRow[]>([]);
  const [total, set_total] = useState(0);
  const [page, set_page] = useState(1);
  const [page_size, set_page_size] = useState(PAGE_SIZE_OPTIONS[0]);
  const move_menu_ref = useRef<HTMLDivElement | null>(null);
  const [search_input, set_search_input] = useState('');
  const [search, set_search] = useState('');
  const [new_group_name, set_new_group_name] = useState('');
  const [moving_product_id, set_moving_product_id] = useState<number | null>(null);
  const [drag_product_id, set_drag_product_id] = useState<number | null>(null);
  const [drop_product_id, set_drop_product_id] = useState<number | null>(null);
  const [drop_group_id, set_drop_group_id] = useState<number | null>(null);
  const [preview, set_preview] = useState<ProductGroupImportPreview | null>(null);
  const [pending_file, set_pending_file] = useState<{ file_base64: string; file_name: string } | null>(
    null
  );
  const [loading, set_loading] = useState(false);
  const [message, set_message] = useState<string | null>(null);
  const [error, set_error] = useState<string | null>(null);

  useEffect(() => {
    if (!auth_loading && !permissions_loading && !has_access) {
      navigate('/products');
    }
  }, [auth_loading, permissions_loading, has_access, navigate]);

  const load_page = useCallback(async () => {
    const [next_groups, next_page] = await Promise.all([
      productGroupService.list_groups(),
      productGroupService.list_product_page(page, page_size, search),
    ]);
    set_groups(next_groups);
    set_rows(next_page.rows);
    set_total(next_page.total);
    if (next_page.rows.length === 0 && page > 1 && next_page.total > 0) {
      set_page(page - 1);
    }
  }, [page, page_size, search]);

  useEffect(() => {
    if (!has_access) return;
    load_page().catch((err: Error) => set_error(err.message));
  }, [has_access, load_page]);

  const page_count = Math.max(1, Math.ceil(total / page_size));

  useEffect(() => {
    if (moving_product_id == null) return;
    const close_menu = (event: MouseEvent) => {
      if (move_menu_ref.current && !move_menu_ref.current.contains(event.target as Node)) {
        set_moving_product_id(null);
      }
    };
    const close_on_escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') set_moving_product_id(null);
    };
    document.addEventListener('mousedown', close_menu);
    document.addEventListener('keydown', close_on_escape);
    return () => {
      document.removeEventListener('mousedown', close_menu);
      document.removeEventListener('keydown', close_on_escape);
    };
  }, [moving_product_id]);
  const group_label = (product_group_id: number, fallback: string) =>
    groups.find((group) => group.product_group_id === product_group_id)?.display_label || fallback;

  const read_file = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Could not read the import file.'));
      reader.onload = () => {
        const result = String(reader.result || '');
        resolve(result.includes(',') ? result.split(',')[1] : result);
      };
      reader.readAsDataURL(file);
    });

  const on_choose_file = async (file: File | null) => {
    if (!file) return;
    set_error(null);
    set_message(null);
    set_loading(true);
    try {
      const file_base64 = await read_file(file);
      const next = await productGroupService.import_file(file_base64, file.name, false);
      set_pending_file({ file_base64, file_name: file.name });
      set_preview(next);
    } catch (err) {
      set_preview(null);
      set_pending_file(null);
      set_error(err instanceof Error ? err.message : 'Import preview failed');
    } finally {
      set_loading(false);
    }
  };

  const confirm_replace = async () => {
    if (!pending_file || !preview) return;
    const lines = [
      `Replace every product group from ${pending_file.file_name}?`,
      'Moves made on this page are replaced by the file.',
      `${preview.product_group_count} product groups, ${preview.matched_product_count} products.`,
      `${preview.needs_resolution.length} item codes need resolution and will not be stored.`,
      `${preview.duplicate_warnings.length} duplicate item codes keep the later product group.`,
      `${preview.returning_to_file.length} products will move back to the file group.`,
      `${preview.removed.length} products are not in the file and will leave their product group.`,
    ];
    if (!window.confirm(lines.join('\n'))) return;
    set_loading(true);
    set_error(null);
    try {
      const applied = await productGroupService.import_file(
        pending_file.file_base64,
        pending_file.file_name,
        true,
        preview.catalog_version
      );
      set_preview(null);
      set_pending_file(null);
      set_message(
        `Updated ${applied.product_group_count} product groups and ${applied.matched_product_count} products.`
      );
      if (page === 1) {
        await load_page();
      } else {
        set_page(1);
      }
    } catch (err) {
      if (err instanceof ApiRequestError && err.http_status === 409) {
        set_preview(null);
        set_pending_file(null);
      }
      set_error(err instanceof Error ? err.message : 'Replace failed');
    } finally {
      set_loading(false);
    }
  };

  const delete_group = async (product_group_id: number, label: string) => {
    if (
      !window.confirm(
        `Delete ${label}? Products in this group stay in the catalog and are removed from the group.`
      )
    ) {
      return;
    }
    set_error(null);
    set_loading(true);
    try {
      await productGroupService.delete_group(product_group_id);
      if (moving_product_id != null) set_moving_product_id(null);
      set_message(`Deleted ${label}.`);
      await load_page();
    } catch (err) {
      set_error(err instanceof Error ? err.message : 'Failed to delete product group');
    } finally {
      set_loading(false);
    }
  };

  const create_group = async () => {
    const name = new_group_name.trim();
    if (!name) return;
    set_error(null);
    set_loading(true);
    try {
      await productGroupService.create_group(name);
      set_new_group_name('');
      await load_page();
    } catch (err) {
      set_error(err instanceof Error ? err.message : 'Failed to create product group');
    } finally {
      set_loading(false);
    }
  };

  const move_product = async (product_id: number, destination_product_group_id: number) => {
    set_error(null);
    set_loading(true);
    try {
      await productGroupService.move_products([product_id], destination_product_group_id);
      set_moving_product_id(null);
      set_message('Moved product.');
      await load_page();
    } catch (err) {
      set_error(err instanceof Error ? err.message : 'Failed to move product');
    } finally {
      set_loading(false);
      set_drag_product_id(null);
      set_drop_product_id(null);
      set_drop_group_id(null);
    }
  };

  const remove_product = async (row: ProductGroupTableRow) => {
    if (!window.confirm(`Delete ${row.product_code} from ${group_label(row.product_group_id, row.product_group_name)}? The product stays in the catalog.`)) {
      return;
    }
    set_error(null);
    set_loading(true);
    try {
      await productGroupService.remove_product(row.product_id);
      set_message(`Removed ${row.product_code} from its product group.`);
      await load_page();
    } catch (err) {
      set_error(err instanceof Error ? err.message : 'Failed to remove product');
    } finally {
      set_loading(false);
    }
  };

  const clear_drag = () => {
    set_drag_product_id(null);
    set_drop_product_id(null);
    set_drop_group_id(null);
  };

  const on_drop_group = (product_group_id: number) => {
    if (loading) return;
    const source = rows.find((row) => row.product_id === drag_product_id);
    clear_drag();
    if (!source || source.product_group_id === product_group_id) return;
    void move_product(source.product_id, product_group_id);
  };

  const sections: { product_group_id: number; label: string; rows: ProductGroupTableRow[] }[] = [];
  rows.forEach((row) => {
    const current = sections[sections.length - 1];
    if (!current || current.product_group_id !== row.product_group_id) {
      sections.push({
        product_group_id: row.product_group_id,
        label: group_label(row.product_group_id, row.product_group_name),
        rows: [row],
      });
    } else {
      current.rows.push(row);
    }
  });

  const shown_group_ids = new Set(sections.map((section) => section.product_group_id));
  if (page === 1) {
    groups
      .filter((group) => group.product_count === 0 && !shown_group_ids.has(group.product_group_id))
      .filter((group) => {
        if (fuzzy_tokens(search).length === 0) return !search;
        return product_label_matches_search(group.display_label, group.name, search);
      })
      .forEach((group) => {
        sections.push({
          product_group_id: group.product_group_id,
          label: group.display_label,
          rows: [],
        });
      });
  }

  if (!auth_loading && !permissions_loading && !has_access) return null;

  const preview_notes = preview
    ? [
        preview.needs_resolution.length > 0
          ? `${preview.needs_resolution.length} item codes are not products`
          : '',
        preview.duplicate_warnings.length > 0
          ? `${preview.duplicate_warnings.length} duplicate item codes keep the later group`
          : '',
        preview.parse_warnings.length > 0
          ? `${preview.parse_warnings.length} rows were skipped`
          : '',
      ].filter((note) => note.length > 0)
    : [];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Product groups</h1>
        <p className="page-subtitle">
          An import replaces every product group. A move you make here lasts until the next import.{' '}
          <Link to="/products">Back to Products</Link>
        </p>
      </div>

      {error && <p className="product-groups-error">{error}</p>}
      {message && <p className="product-groups-message">{message}</p>}

      <section className="product-groups-panel">
        <div className="product-groups-toolbar">
          <label>
            Import CSV or Excel
            <input
              type="file"
              disabled={loading}
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(event) => {
                const file = event.target.files?.[0] || null;
                event.target.value = '';
                void on_choose_file(file);
              }}
            />
          </label>
          <input
            value={new_group_name}
            onChange={(event) => set_new_group_name(event.target.value)}
            placeholder="New product group"
            aria-label="New product group"
            disabled={loading}
          />
          <button
            className="btn btn-secondary"
            type="button"
            disabled={loading || new_group_name.trim().length === 0}
            onClick={() => void create_group()}
          >
            Add product group
          </button>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search product group, name, or SKU..."
            aria-label="Search product group, name, or SKU"
            value={search_input}
            onChange={(event) => set_search_input(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              set_page(1);
              set_search(search_input.trim());
            }}
            style={{ flex: 1, padding: '0.5rem' }}
          />
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => {
              set_page(1);
              set_search(search_input.trim());
            }}
          >
            Search
          </button>
          {search_input && (
            <button
              className="btn btn-outline"
              type="button"
              onClick={() => {
                set_search_input('');
                set_page(1);
                set_search('');
              }}
            >
              Clear
            </button>
          )}
        </div>

        {preview && pending_file && (
          <div className="product-groups-preview">
            <p>
              {preview.product_group_count} product groups, {preview.matched_product_count} matched
              products.
              {preview_notes.length === 0 ? ' No items need resolution.' : ` ${preview_notes.join('. ')}.`}
            </p>
            <button
              className="btn btn-primary"
              type="button"
              disabled={
                loading ||
                preview.product_group_count === 0 ||
                preview.matched_product_count === 0 ||
                preview.parse_warnings.length > 0
              }
              onClick={() => void confirm_replace()}
            >
              Replace every product group
            </button>
            {preview.parse_warnings.length > 0 && (
              <p>Fix the skipped rows before replacing.</p>
            )}
            {preview.needs_resolution.length > 0 && (
              <>
                <h3>Needs resolution</h3>
                <p>These item codes are not products. They will not be stored. The matched products will be imported.</p>
                <div className="product-groups-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Item</th>
                        <th>Description</th>
                        <th>Product group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.needs_resolution.map((row) => (
                        <tr key={`${row.file_row}-${row.item_code}`}>
                          <td>{row.file_row}</td>
                          <td>{row.item_code}</td>
                          <td>{row.description}</td>
                          <td>{row.product_group_display_label}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {preview.duplicate_warnings.length > 0 && (
              <>
                <h3>Duplicate item codes</h3>
                <ul>
                  {preview.duplicate_warnings.map((row) => (
                    <li key={`${row.file_row}-${row.item_code}`}>
                      {row.item_code} stays in {row.kept_display_label} (row {row.file_row}). Dropped
                      from {row.dropped_display_label}.
                    </li>
                  ))}
                </ul>
              </>
            )}
            {preview.returning_to_file.length > 0 && (
              <>
                <h3>Will move back to the file group</h3>
                <ul>
                  {preview.returning_to_file.map((row) => (
                    <li key={row.product_id}>
                      {row.product_code} {row.product_name}: {row.current_display_label} to{' '}
                      {row.file_display_label}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {preview.removed.length > 0 && (
              <>
                <h3>Will leave their product group</h3>
                <p>These products are not in the file. They stay in the catalog.</p>
                <ul>
                  {preview.removed.map((row) => (
                    <li key={row.product_id}>
                      {row.product_code} {row.product_name} is in {row.current_display_label}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {preview.parse_warnings.length > 0 && (
              <ul>
                {preview.parse_warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section className="product-groups-panel">
        <table className="product-groups-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sections.length === 0 && (
              <tr>
                <td colSpan={3}>No products in a product group.</td>
              </tr>
            )}
            {sections.map((section) => {
              const full_count =
                groups.find((group) => group.product_group_id === section.product_group_id)
                  ?.product_count ?? section.rows.length;
              return (
                <React.Fragment key={section.product_group_id}>
                  <tr
                    className={
                      drop_group_id === section.product_group_id
                        ? 'product-groups-divider is-drop-target'
                        : 'product-groups-divider'
                    }
                    onDragOver={(event) => {
                      if (drag_product_id == null) return;
                      event.preventDefault();
                      set_drop_product_id(null);
                      set_drop_group_id(section.product_group_id);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      on_drop_group(section.product_group_id);
                    }}
                  >
                    <td colSpan={3}>
                      <div className="product-groups-divider-row">
                        <span>
                          {section.label}
                          <span className="product-groups-count">{full_count}</span>
                        </span>
                        <button
                          type="button"
                          className="pg-btn pg-btn-delete"
                          aria-label={`Delete product group ${section.label}`}
                          disabled={loading}
                          onClick={() => void delete_group(section.product_group_id, section.label)}
                        >
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {section.rows.length === 0 && (
                    <tr>
                      <td colSpan={3} className="product-groups-empty">
                        No products. Drop a product here to add it.
                      </td>
                    </tr>
                  )}
                  {section.rows.map((row) => (
                    <tr
                      key={row.product_id}
                      draggable={!loading}
                      onDragStart={(event) => {
                        event.dataTransfer.setData('text/plain', String(row.product_id));
                        event.dataTransfer.effectAllowed = 'move';
                        set_drag_product_id(row.product_id);
                      }}
                      onDragEnd={clear_drag}
                      onDragOver={(event) => {
                        if (drag_product_id == null || drag_product_id === row.product_id) return;
                        event.preventDefault();
                        set_drop_group_id(null);
                        set_drop_product_id(row.product_id);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        on_drop_group(row.product_group_id);
                      }}
                      className={drop_product_id === row.product_id ? 'is-drop-target' : ''}
                    >
                      <td>{row.product_code}</td>
                      <td>{row.name}</td>
                      <td
                        className={
                          moving_product_id === row.product_id
                            ? 'product-groups-actions is-open'
                            : 'product-groups-actions'
                        }
                      >
                        <div
                          className="pg-move"
                          ref={moving_product_id === row.product_id ? move_menu_ref : undefined}
                        >
                          <button
                            type="button"
                            className="pg-btn pg-btn-move"
                            aria-label={`Move ${row.product_code} to another product group`}
                            disabled={loading}
                            aria-expanded={moving_product_id === row.product_id}
                            onClick={() =>
                              set_moving_product_id((current) =>
                                current === row.product_id ? null : row.product_id
                              )
                            }
                          >
                            <ArrowForwardIcon style={{ fontSize: 16 }} />
                            Move
                          </button>
                          {moving_product_id === row.product_id && (
                            <ul className="pg-move-menu" role="listbox" aria-label="Product groups">
                              {groups.filter((group) => group.product_group_id !== row.product_group_id)
                                .length === 0 && (
                                <li className="pg-move-empty">No other product groups</li>
                              )}
                              {groups
                                .filter((group) => group.product_group_id !== row.product_group_id)
                                .map((group) => (
                                  <li key={group.product_group_id}>
                                    <button
                                      type="button"
                                      role="option"
                                      disabled={loading}
                                      onClick={() =>
                                        void move_product(row.product_id, group.product_group_id)
                                      }
                                    >
                                      {group.display_label}
                                    </button>
                                  </li>
                                ))}
                            </ul>
                          )}
                        </div>
                        <button
                          type="button"
                          className="pg-btn pg-btn-delete"
                          aria-label={`Delete ${row.product_code} from its product group`}
                          disabled={loading}
                          onClick={() => void remove_product(row)}
                        >
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {total > 0 && (
          <div className="product-groups-pager">
            <div className="product-groups-pager-count">
              Showing {(page - 1) * page_size + 1} to {Math.min(page * page_size, total)} of {total}{' '}
              products
            </div>
            <div className="product-groups-pager-controls">
              <label>
                Show:
                <select
                  aria-label="Rows per page"
                  value={page_size}
                  onChange={(event) => {
                    set_page_size(parseInt(event.target.value, 10));
                    set_page(1);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => set_page((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span>
                Page {page} of {page_count}
              </span>
              <button
                type="button"
                disabled={page >= page_count}
                onClick={() => set_page((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      <style>{`
        .product-groups-error { color: #b91c1c; }
        .product-groups-message { color: #166534; }
        .product-groups-panel {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem 1.25rem;
          margin-top: 1rem;
        }
        .product-groups-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: end;
        }
        .product-groups-toolbar label { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.875rem; }
        .product-groups-toolbar input {
          padding: 0.45rem 0.6rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
        }
        .product-groups-preview { margin-top: 1rem; }
        .product-groups-scroll { max-height: 240px; overflow: auto; }
        .product-groups-table, .product-groups-preview table { width: 100%; border-collapse: collapse; }
        .product-groups-table th, .product-groups-table td,
        .product-groups-preview th, .product-groups-preview td {
          text-align: left;
          padding: 0.55rem 0.65rem;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: middle;
        }
        .product-groups-divider td {
          background: #f8fafc;
          font-weight: 600;
          color: #111827;
          border-top: 1px solid #e5e7eb;
        }
        .product-groups-divider-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
        }
        .product-groups-count {
          margin-left: 0.5rem;
          font-weight: 500;
          color: #6b7280;
          font-size: 0.8125rem;
        }
        .product-groups-empty { color: #6b7280; font-size: 0.875rem; }
        .product-groups-table tr[draggable="true"] { cursor: grab; }
        .product-groups-table tr.is-drop-target td { background: #eff6ff; }
        .product-groups-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
          white-space: nowrap;
          position: relative;
        }
        .product-groups-actions.is-open { z-index: 5; }
        .pg-move { position: relative; }
        .pg-move-menu {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          z-index: 6;
          min-width: 220px;
          max-height: 240px;
          margin: 0;
          padding: 0.25rem 0;
          list-style: none;
          background: #fff;
          border: 1px solid #d0d5dd;
          border-radius: 8px;
          box-shadow: 0 8px 16px rgba(16, 24, 40, 0.12);
          overflow: auto;
        }
        .pg-move-menu button {
          display: block;
          width: 100%;
          padding: 0.45rem 0.75rem;
          border: 0;
          background: #fff;
          color: #344054;
          text-align: left;
          font-size: 0.875rem;
          cursor: pointer;
        }
        .pg-move-menu button:hover { background: #f9fafb; }
        .pg-move-empty { padding: 0.45rem 0.75rem; color: #6b7280; font-size: 0.875rem; }
        .pg-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.35rem 0.7rem;
          background: #fff;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          line-height: 1.25;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
        }
        .pg-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .pg-btn-move {
          border: 1px solid #d0d5dd;
          color: #344054;
        }
        .pg-btn-move:hover { background: #f9fafb; }
        .pg-btn-delete {
          border: 1px solid #fda29b;
          color: #d92d20;
        }
        .pg-btn-delete:hover { background: #fef3f2; }
        .product-groups-pager {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e1e5e9;
          flex-wrap: wrap;
        }
        .product-groups-pager-count { font-size: 0.875rem; color: #6b7280; }
        .product-groups-pager-controls { display: flex; align-items: center; gap: 0.5rem; }
        .product-groups-pager-controls label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #6b7280;
        }
        .product-groups-pager-controls select {
          padding: 0.25rem 0.5rem;
          font-size: 0.875rem;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
        }
        .product-groups-pager-controls button {
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          color: #374151;
          cursor: pointer;
        }
        .product-groups-pager-controls button:disabled {
          background: #f9fafb;
          color: #9ca3af;
          cursor: not-allowed;
        }
        .product-groups-pager-controls span {
          font-size: 0.875rem;
          color: #6b7280;
          padding: 0 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default ProductGroupsPage;
